import { useMutation, useQuery } from "@apollo/client";
import moment from "moment";
import React, { FunctionComponent, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { ClipLoader } from "react-spinners";
import { Product, ProductionReport } from "../../global/types";
import Table from "../../presenters/Table";
import { columns as productColumns } from "./config/table/productsTable";
import {
  ORDERS_COUNT,
  PRODUCTION_REPORTS,
  PRODUCTS,
  UPDATE_PRODUCT_AMOUNT_BULK,
} from "./gql";
import {
  GetOrdersCountQuery,
  GetOrdersCountQueryVariables,
  GetProductionReportsQuery,
  GetProductsQuery,
  GetProductsQueryVariables,
  UpdateProductAmountBulkMutation,
  UpdateProductAmountBulkMutationVariables,
} from "./gql/index.generated";
import ReactDatePicker from "react-datepicker";

import "./index.scss";

export type ProductsState = {
  value: null | Product[];
  dirty: boolean;
  previousValue: null | Product[];
};

export type ReducedProducts = {
  [key: number]: Product;
};

const INVALID_AMOUNT_ERROR =
  "Ungültige Menge in der Spalte „Produziert“. Bitte den Wert korrigieren und erneut speichern.";
const SAVE_FAILED_ERROR =
  "Speichern fehlgeschlagen. Bitte die Mengen prüfen und erneut versuchen.";

const Main: FunctionComponent = () => {
  const [selectedProductionReport, setSelectedProductionReport] =
    useState<ProductionReport>();

  const [isSidebarShowing, setIsSidebarShowing] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date(moment().add(1, "day").format("YYYY-MM-DD"))
  );
  const { data } = useQuery<GetProductionReportsQuery>(PRODUCTION_REPORTS);
  const { data: ordersCountData, loading: ordersCountLoading } = useQuery<
    GetOrdersCountQuery,
    GetOrdersCountQueryVariables
  >(ORDERS_COUNT, {
    variables: {
      date: moment(selectedDate).format("YYYY-MM-DD"),
    },
    skip: !selectedDate,
  });
  const [updateAmountBulk, { loading: updateAmountLoading }] = useMutation<
    UpdateProductAmountBulkMutation,
    UpdateProductAmountBulkMutationVariables
  >(UPDATE_PRODUCT_AMOUNT_BULK);
  const { data: productsData, loading: productsLoading } = useQuery<
    GetProductsQuery,
    GetProductsQueryVariables
  >(PRODUCTS, {
    variables: {
      productionReportId: selectedProductionReport?.id as number,
      date: moment(selectedDate).format("YYYY-MM-DD"),
    },
    fetchPolicy: "network-only",
    skip:
      !selectedProductionReport ||
      !selectedProductionReport.id ||
      !selectedDate,
  });
  const [productsState, setProductsState] = useState<ProductsState>({
    value: null,
    dirty: false,
    previousValue: null,
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleNewRow = (key: string, value: number) => {
    localStorage.setItem(key, JSON.stringify(value));
    refreshProductsState();
  };

  const fromLocalStorage = (itemKey: string = ""): number => {
    const lsItem = localStorage.getItem(itemKey);
    const result = lsItem ? JSON.parse(lsItem) : 0;
    return result;
  };

  useEffect(() => {
    const value = productsData?.getProducts
      ? productsData?.getProducts.map((e) => {
          const itemKey = `${selectedProductionReport?.id}_${e.id}_${moment(
            selectedDate
          ).format("YYYY-MM-DD")}`;
          const toProduce = fromLocalStorage(itemKey);
          return { ...e, toProduce, itemKey: itemKey };
        })
      : null;
    setProductsState({
      value,
      dirty: false,
      previousValue: null,
    });
    setSaveError(null);
  }, [productsData, selectedDate, selectedProductionReport?.id]);

  const refreshProductsState = () => {
    const value = productsData?.getProducts
      ? productsData?.getProducts.map((e) => {
          const itemKey = `${selectedProductionReport?.id}_${e.id}_${moment(
            selectedDate
          ).format("YYYY-MM-DD")}`;
          const toProduce = fromLocalStorage(itemKey);
          return { ...e, toProduce, itemKey: itemKey };
        })
      : null;
    setProductsState((prevData) => ({ ...prevData, value }));
  };

  const handleAmountUpdate = async (productId: number, amount: number) => {
    const { value: products } = productsState;
    if (!products || !Number.isFinite(amount)) return;
    const productsCopy = products.map((item) => cloneDeep(item));
    const newProducts = productsCopy.map((item) => cloneDeep(item));
    const idx = newProducts.findIndex((item) => item.id === productId);
    if (productsCopy[idx].amount === amount) return;
    newProducts[idx].amount = amount;
    setProductsState({
      value: newProducts,
      dirty: true,
      previousValue: productsCopy,
    });
  };

  const handleSave = async () => {
    if (!productsState.dirty || !productsState.value || !productsData) return;
    const date = moment(selectedDate).format("YYYY-MM-DD");
    const reducedProducts: ReducedProducts = productsState.value.reduce(
      (acc, item) => ({ ...acc, [item.id]: item }),
      {}
    );
    const reducedOriginalProducts: ReducedProducts =
      productsData.getProducts.reduce(
        (acc, item) => ({ ...acc, [item.id]: item }),
        {}
      );
    const productKeys = Object.keys(reducedProducts);
    const dirtyProducts: Product[] = [];
    for (const key of productKeys) {
      const parsedKey = parseInt(key);
      const productAmount = reducedProducts[parsedKey]?.amount;
      const originalProductAmount = reducedOriginalProducts[parsedKey]?.amount;
      if (productAmount !== originalProductAmount) {
        dirtyProducts.push(reducedProducts[parsedKey]);
      }
    }
    const args = dirtyProducts.map((item) => ({
      amount: item.amount,
      productId: item.id,
      date,
    }));
    if (args.some((item) => !Number.isFinite(item.amount))) {
      setSaveError(INVALID_AMOUNT_ERROR);
      return;
    }
    setSaveError(null);
    try {
      const res = await updateAmountBulk({ variables: { args } });
      if (!res.data?.updateProductAmountBulk) {
        setSaveError(SAVE_FAILED_ERROR);
        return;
      }
      setProductsState((state) => ({
        value: state.value,
        dirty: false,
        previousValue: null,
      }));
    } catch {
      setSaveError(SAVE_FAILED_ERROR);
    }
  };

  const handleCancel = () => {
    if (!productsState.previousValue) return;
    setSaveError(null);
    setProductsState((state) => ({
      value: state.previousValue!.slice(0),
      dirty: false,
      previousValue: null,
    }));
  };

  if (updateAmountLoading) {
    return (
      <div className='app-loader'>
        <ClipLoader size={200} />
      </div>
    );
  }

  // input with readOnly prop for delete native keyboard on mobile
  const DatepickerInput = React.forwardRef((props, ref: any) => (
    <input ref={ref} type='text' {...props} readOnly />
  ));

  return (
    <div>
      <div className='columns'>
        <div className='column is-one-third no-padding-bottom'>
          <ReactDatePicker
            locale='de'
            customInput={<DatepickerInput />}
            withPortal
            dateFormat={"EEEE, dd.MM.yyyy"}
            selected={selectedDate}
            onChange={(date) => date && setSelectedDate(date as Date)}
          />
          {!ordersCountLoading ? (
            <div className='orders-info'>
              <p>
                Bestellungen:{" "}
                <span className='green bold'>
                  {ordersCountData?.ordersCount}
                </span>
              </p>
            </div>
          ) : (
            <div className='orders-loader'>
              <ClipLoader />
            </div>
          )}
        </div>
        <div className='column is-two-third no-padding-bottom'>
          <h2 className='title is-5 uppercase'>
            {selectedProductionReport?.name || "Choose production report"}
          </h2>
          <div className='action-buttons'>
            <button
              onClick={handleCancel}
              className='main-button main-button-gray'
              disabled={
                !productsState.value ||
                productsState.value.length < 0 ||
                !productsState.previousValue
              }
            >
              Löschen
            </button>
            <button
              onClick={handleSave}
              className='main-button main-button-green'
              disabled={
                !productsState.value ||
                productsState.value.length < 0 ||
                !productsState.dirty
              }
            >
              Speichern
            </button>
          </div>
          {saveError && (
            <div className='notification is-danger is-light save-error'>
              <button
                type='button'
                className='delete'
                aria-label='Fehlermeldung schliessen'
                onClick={() => setSaveError(null)}
              />
              {saveError}
            </div>
          )}
        </div>
      </div>
      <div className='columns'>
        {data && isSidebarShowing && (
          <div className='column is-one-third'>
            <Table
              columns={[{ key: "name", name: "Produktionsliste" }]}
              data={data.getProductionReports}
              onRowClick={(row) => setSelectedProductionReport(row)}
              activeItemId={selectedProductionReport?.id}
              defaultSorted={{ columnKey: "name", direction: "ASC" }}
            />
          </div>
        )}
        <div className='column is-two-third'>
          {!productsLoading ? (
            productsState.value && productsState.value.length > 0 ? (
              <Table
                key={`table_${isSidebarShowing}`}
                columns={productColumns({
                  onAmountChange: handleAmountUpdate,
                  handleNewRow,
                })}
                data={productsState.value}
              />
            ) : (
              <h3 className='title is-6 centered mt-5 uppercase'>
                No products found
              </h3>
            )
          ) : (
            <div className='products-loader'>
              <ClipLoader size={100} />
            </div>
          )}
        </div>
        {data && (
          <div className='floating-button'>
            <button
              onClick={() => setIsSidebarShowing((prevValue) => !prevValue)}
              className='main-button main-button-gray'
              disabled={!!(productsLoading || !productsState?.value?.length)}
            >
              {isSidebarShowing ? "Zeige weniger" : "Zeig mehr"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Main;
